import { GraphQLClient } from "graphql-hooks";
import useGQLQueryWithClient from '../../hooks/useGQLQueryWithClient';
import { useState } from 'react';
import './RenderWebshopArticle.css';
import WebshopNumericInput from '../WebshopNumericInput';
import { getGeneralTranslations, getLocation, getSKU, getWebshopItem } from './queries';
import { MetaInfo, WebshopItem } from './types';
import WhatIsParagraph from './WhatIsParagraph';
import MarketingParagraph from './MarketingParagraph';

type PropTypes = {
  width: number,
  height: number,
  language: string,
  id: string,
  datoplugin : boolean,
};

function RenderWebshopArticle({ width, height, language, id, datoplugin }: PropTypes) {
  const client = new GraphQLClient({
    url: "https://graphql.datocms.com/",
    headers: {
      "Authorization": `Bearer c2637bf4c3ca322961249cf1b65310`, // Todo use user token
      "X-Include-Drafts": "true",
    }
  });

  const [meta, setMeta] = useState<MetaInfo | null>(null);

  const locationCode = language.split('_')[0].toLowerCase();

  const { loading: loadingWebshopItem, error: errorWebshopItem, data: webshopItemData } = useGQLQueryWithClient(client, getWebshopItem(id, language));
  const { loading: loadingGeneralTranslations, error: errorGeneralTranslations, data: generalTranslationsData } = useGQLQueryWithClient(client, getGeneralTranslations(language));
  const { loading: loadingLocation, error: errorLocation, data: locationData } = useGQLQueryWithClient(client, getLocation(locationCode));
  const { loading: loadingSku, error: errorSku, data: skuData } = useGQLQueryWithClient(client, getSKU(meta?.stockKeepingUnit.id ?? "", language), {}, meta?.stockKeepingUnit === null);

  if (loadingWebshopItem || loadingGeneralTranslations || loadingLocation) return <div>"Loading..."</div>;
  if (errorWebshopItem || errorGeneralTranslations || errorLocation || errorSku) return <div>"Error..."</div>;


  const webshopItem = ((webshopItemData.data as any)?.productPage ?? {}) as WebshopItem;
  const generalTranslations = (generalTranslationsData.data as any)?.generalProductPage as { putInBasket: string, packaging: string, dosingTitle: string, ingredientsNrvExplanation: string, ingredientsTitle: string, ingredientsQuantityHeader: string };
  const location = (locationData.data as any)?.location as { id: string, locationCode: string, currency: string, currencyLocalized: string };

  if (location === null) alert(`The location for '${locationCode}' is missing.`);

  if (meta === null) {
    for (const metaTmp of webshopItem.metainfo) {
      if (metaTmp.location.id === location.id) {
        setMeta(metaTmp);
        break;
      }
    }
    return <div>Waiting for SKU to be selected</div>;
  }

  if (loadingSku) return <div>"Loading SKU..."</div>;
  if ((skuData.data as any)?.stockKeepingUnit === null) return <div>SKU is still null, so waiting...</div>;

  const sku = (skuData.data as any)?.stockKeepingUnit as { packaging: string, packagingLongName: string };

  return (<>
    {(datoplugin) && <div id="url">
      <a href={`https://fhc-datoplugins.com/webshop/viewer/item?language=${language}&id=${id}`} target="_blank">Open in separate window</a>
    </div>}
    <div id="parent-div" className='parent-div'>
      <div className="main-title" style={{ backgroundImage: `url(${webshopItem.titleImage.url})` }}>
        <div className="title-section" style={{ position: "relative", left: "50%", width: "49%", paddingTop: "100px" }}>
          <h2>{webshopItem.productName} {sku.packaging}</h2>
          <h1>{webshopItem.title}</h1>
          <span>{webshopItem.subtitle}</span>
          <table>
            <tr><td><span>{generalTranslations.packaging}</span></td> <td rowSpan={2}><span className="product-price">{meta.price}</span> <span>{location.currencyLocalized} </span> <span className="product-price">&nbsp;&nbsp;&gt;</span> </td></tr>
            <tr><td><span><b>{sku.packagingLongName}</b></span></td> </tr>
          </table>
          <div style={{ display: "flex", width: "100%", marginTop: "30px", paddingBottom: "100px" }}>
            <div style={{ flex: 0.3, display: 'flex', justifyContent: 'left', marginRight: "10px" }}>
              <WebshopNumericInput />
            </div>
            <div style={{ flex: 0.6, display: 'flex', justifyContent: 'right', marginLeft: "10px" }}>
              <button className='to-basket' style={{ width: "100%" }}>{generalTranslations.putInBasket}</button>
            </div>
          </div>
        </div>
      </div>
      <div className="section narrow colored-background">
        <div className='preface padding narrow'>{webshopItem.preface}</div>
      </div>
      <div className="section what-is">
        <div className="padding narrow">
          <h1>{webshopItem.whatIsTitle}</h1>
          {webshopItem.whatIsParagraphs.map(text => <WhatIsParagraph text={text.text} />)}
        </div>
      </div>
      <div className="section marketing">
        {webshopItem.marketingInfo.map((p, i) => <MarketingParagraph title={p.title} focalPoint={p.picture?.focalPoint} text={p.text} url={p.picture?.url} left={i % 2 == 0} />)}
      </div>
      <div className="section dosing padding colored-background">
        <div style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'top'
        }}>
          <div style={{ flex: 0.7, marginLeft: "20px"}}>
            <h1>{generalTranslations.dosingTitle}</h1>
          </div>
          <div style={{ flex: 1.3 }}>
            {webshopItem.dosingParagraphs.map(text => <div className='content'>{text.text}</div>)}
          </div>
        </div>
      </div>
      <div className='section ingredients' style={{ backgroundImage: `url(${webshopItem.ingredientsBackground.url})` }}>
        <div>
          <h1>{generalTranslations.ingredientsTitle}</h1>
        </div>
        <div style={{ width: "40%" }}>
          <table>
            <thead>
              <tr>
                <th></th>
                <th>{generalTranslations.ingredientsQuantityHeader}</th>
                <th>%NRV*</th>
              </tr>
            </thead>
            <tbody>
              {
                webshopItem.ingredients?.map((ingredient) => <tr>
                  <td>{ingredient.name}</td>
                  <td><b>{ingredient.quantity}</b> mg</td>
                  <td>{ingredient.nrv ?? '-'}</td>
                </tr>)
              }
            </tbody>
          </table>
          {generalTranslations.ingredientsNrvExplanation}
        </div>
      </div>
    </div>
    </>);
}

export default RenderWebshopArticle;